/**
 * MaterialsReceiptConfirm · iter303ak-fix29 — Work In Progress · T10 · Action 10
 * ──────────────────────────────────────────────────────────────────────────────
 * Per child × supplier from `/materials-overview`:
 *   · Delivery location dropdown: site / office / pickup
 *   · Received checkbox — auto-stamps `received_at` on tick
 *   · Optional note (batch #, condition, damage)
 * Persists on Continue to parent appraisal `materials_receipt` map.
 * Continue blocked until every row is marked received.
 */
import React, { useState, useEffect, useMemo, useCallback } from 'react';
import axios from 'axios';
import { CheckCircle2, Package2, Truck, MapPin, Home, Hand, AlertTriangle } from 'lucide-react';

const api = axios.create({ baseURL: `${process.env.REACT_APP_BACKEND_URL}/api` });
api.interceptors.request.use((cfg) => {
  const t = localStorage.getItem('access_token');
  if (t) cfg.headers.Authorization = `Bearer ${t}`;
  return cfg;
});

// Local slugify — mirror the backend's `routes.supplier_letterheads.slugify`.
// materials-overview returns `{ supplier: "Roofing Industries", line_count, materials }`
// without a slug — we compute one client-side for keying the receipt map.
const slugify = (s) => (s || '')
  .toString()
  .toLowerCase()
  .replace(/[^a-z0-9]+/g, '-')
  .replace(/^-+|-+$/g, '') || 'unknown';

const LOCATIONS = [
  { key: 'site',   label: 'Delivered to site',   Icon: Truck },
  { key: 'office', label: 'Delivered to office', Icon: Home },
  { key: 'pickup', label: 'Picked up',           Icon: Hand },
];

export default function MaterialsReceiptConfirm({ action, capturedData, onNext, onBack, onCancel, canBack, value, onChange }) {
  const params = action.params || {};
  const parentAppraisalId = capturedData?.appraisal_id;

  const acceptedChildren = useMemo(() => {
    const decisions = capturedData?.appraisal_record?.child_decisions
      || capturedData?.appraisal?.child_decisions
      || {};
    return Object.entries(decisions)
      .filter(([, d]) => d?.decision === 'accepted')
      .map(([child_id, d]) => ({ child_id, ...d }));
  }, [capturedData]);

  // Prior receipts from parent so re-opening the run doesn't wipe data.
  const priorReceipts = useMemo(() => {
    return capturedData?.appraisal_record?.materials_receipt
      || capturedData?.appraisal?.materials_receipt
      || {};
  }, [capturedData]);

  const [rows, setRows] = useState([]);      // { child_id, appraisal_no, quote_type_ref, supplier_slug, supplier_name }
  const [receipts, setReceipts] = useState(() => value?.materials_receipt || priorReceipts || {});
  const [loading, setLoading] = useState(true);
  const [saveErr, setSaveErr] = useState('');
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    if (!acceptedChildren.length) { setLoading(false); return; }
    setLoading(true);
    const list = [];
    const nextReceipts = { ...receipts };
    for (const c of acceptedChildren) {
      try {
        const { data } = await api.get(`/appraisals/${c.child_id}/materials-overview`);
        const suppliers = Array.isArray(data?.suppliers) ? data.suppliers : [];
        for (const s of suppliers) {
          const slug = s.slug || slugify(s.supplier);
          const name = s.supplier || slug;
          if (!slug) continue;
          list.push({
            child_id: c.child_id,
            appraisal_no: c.appraisal_no,
            quote_type_ref: c.quote_type_ref,
            variant_label: c.variant_label,
            supplier_slug: slug,
            supplier_name: name,
          });
          nextReceipts[c.child_id] = nextReceipts[c.child_id] || {};
          if (!nextReceipts[c.child_id][slug]) {
            nextReceipts[c.child_id][slug] = { location: 'site', received: false, note: '' };
          }
        }
      } catch { /* silent */ }
    }
    setRows(list);
    setReceipts(nextReceipts);
    setLoading(false);
  }, [acceptedChildren]);   

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (onChange) onChange({ materials_receipt: receipts });
  }, [receipts]);   

  const patchReceipt = (child_id, supplier_slug, patch) => {
    setReceipts(prev => ({
      ...prev,
      [child_id]: {
        ...(prev[child_id] || {}),
        [supplier_slug]: { ...((prev[child_id] || {})[supplier_slug] || {}), ...patch },
      },
    }));
  };

  const toggleReceived = (child_id, supplier_slug) => {
    const cur = (receipts[child_id] || {})[supplier_slug] || {};
    if (cur.received) {
      patchReceipt(child_id, supplier_slug, { received: false, received_at: null });
    } else {
      patchReceipt(child_id, supplier_slug, { received: true, received_at: new Date().toISOString() });
    }
  };

  const persistAndContinue = async () => {
    setSaving(true);
    setSaveErr('');
    try {
      if (parentAppraisalId) {
        await api.patch(`/appraisals/${parentAppraisalId}`, { materials_receipt: receipts });
      }
      onNext();
    } catch (e) {
      setSaveErr(e?.response?.data?.detail || e?.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const totalRows = rows.length;
  const receivedRows = rows.filter(r => ((receipts[r.child_id] || {})[r.supplier_slug] || {}).received).length;
  const allReceived = totalRows > 0 && receivedRows === totalRows;

  return (
    <div className="space-y-4" data-testid="wf-materials-receipt-confirm">
      <div className="rounded-xl border border-amber-200 bg-amber-50/60 p-4">
        <div className="flex items-start gap-3">
          <Package2 className="w-5 h-5 mt-0.5 shrink-0 text-amber-700" />
          <div className="flex-1">
            <div className="text-sm font-semibold text-amber-900">Confirm Materials Received</div>
            <div className="text-sm mt-1 text-amber-800">
              {params.prompt || 'Confirm every supplier delivery. Pick location + tick received.'}
            </div>
          </div>
        </div>
      </div>

      {loading && <div className="text-sm text-slate-500 italic p-3">Loading suppliers…</div>}

      {!loading && rows.length === 0 && (
        <div className="p-4 bg-amber-50 border border-amber-200 rounded text-sm text-amber-800">
          No suppliers found on accepted quotes. Set up materials in the child Materials tab first.
        </div>
      )}

      {rows.length > 0 && (
        <>
          <div className="flex items-center justify-between p-3 rounded-lg border border-slate-200 bg-slate-50">
            <div className="text-xs text-slate-600">
              <b className="text-slate-800">{receivedRows}</b> of <b className="text-slate-800">{totalRows}</b> received
            </div>
          </div>
          <div className="space-y-2">
            {rows.map(r => {
              const key = `${r.child_id}|${r.supplier_slug}`;
              const rec = (receipts[r.child_id] || {})[r.supplier_slug] || {};
              const received = !!rec.received;
              const label = `${r.quote_type_ref || ''}${r.variant_label ? ` · ${r.variant_label}` : ''} — ${r.supplier_name}`;
              return (
                <div key={key} className={`p-3 border rounded-lg ${received ? 'bg-emerald-50 border-emerald-300' : 'bg-white border-slate-200'}`}
                  data-testid={`wf-mr-row-${r.child_id}-${r.supplier_slug}`}>
                  <div className="flex items-center gap-2 flex-wrap mb-2">
                    <span className="text-sm font-medium text-slate-800">{label}</span>
                    {r.appraisal_no && <span className="text-[10px] font-bold uppercase tracking-widest text-indigo-800 bg-indigo-100 border border-indigo-300 rounded px-1.5 py-0.5">A{r.appraisal_no}</span>}
                    {received && (
                      <span className="text-[10px] font-semibold text-emerald-800 bg-emerald-100 border border-emerald-300 rounded px-1.5 py-0.5 inline-flex items-center gap-1">
                        <CheckCircle2 size={10} /> Received
                      </span>
                    )}
                  </div>
                  <div className="pl-3 border-l-2 border-slate-200 space-y-2">
                    <div className="flex items-center gap-2 flex-wrap">
                      {LOCATIONS.map(l => {
                        const active = rec.location === l.key;
                        const Icon = l.Icon;
                        return (
                          <button key={l.key} type="button"
                            onClick={() => patchReceipt(r.child_id, r.supplier_slug, { location: l.key })}
                            disabled={received}
                            data-testid={`wf-mr-loc-${r.child_id}-${r.supplier_slug}-${l.key}`}
                            className={`px-2.5 py-1 rounded-full text-[11px] font-semibold border inline-flex items-center gap-1 transition-all
                              ${active ? 'bg-sky-600 text-white border-sky-700' : 'bg-white text-slate-700 border-slate-300 hover:bg-slate-50'}
                              disabled:opacity-60 disabled:cursor-not-allowed`}>
                            <Icon size={12} /> {l.label}
                          </button>
                        );
                      })}
                    </div>
                    <input type="text" value={rec.note || ''}
                      onChange={(e) => patchReceipt(r.child_id, r.supplier_slug, { note: e.target.value })}
                      disabled={received}
                      placeholder="Optional note (batch #, condition, damage…)"
                      data-testid={`wf-mr-note-${r.child_id}-${r.supplier_slug}`}
                      className="w-full px-2 py-1 text-xs border border-slate-300 rounded disabled:opacity-60" />
                    <div className="flex items-center justify-between gap-2">
                      <div className="text-[10px] text-slate-500">
                        {received && rec.received_at && <span className="text-emerald-700">✓ Received {new Date(rec.received_at).toLocaleString()}</span>}
                      </div>
                      <button onClick={() => toggleReceived(r.child_id, r.supplier_slug)}
                        data-testid={`wf-mr-received-btn-${r.child_id}-${r.supplier_slug}`}
                        className={`px-3 py-1.5 rounded-md text-xs font-semibold inline-flex items-center gap-1.5
                          ${received ? 'bg-white text-slate-700 border border-slate-300 hover:bg-slate-50' : 'bg-emerald-600 text-white hover:bg-emerald-700'}`}>
                        <CheckCircle2 size={12} /> {received ? 'Undo' : 'Mark received'}
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </>
      )}

      {saveErr && (
        <div className="flex items-start gap-2 text-sm text-rose-700 bg-rose-50 border border-rose-200 rounded p-3">
          <AlertTriangle className="w-4 h-4 mt-0.5" /> <span>{saveErr}</span>
        </div>
      )}

      <div className="flex items-center justify-between gap-3 pt-2">
        {canBack ? <button onClick={onBack} className="px-3 py-1.5 rounded-md text-sm text-slate-600 hover:bg-slate-100">← Back</button> : <span />}
        <div className="flex gap-2">
          {onCancel && <button onClick={onCancel} className="px-3 py-1.5 rounded-md text-sm text-slate-600 hover:bg-slate-100">Cancel</button>}
          <button onClick={persistAndContinue} disabled={!allReceived || saving}
            data-testid="wf-mr-continue"
            className="px-4 py-1.5 rounded-md text-sm bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-40 inline-flex items-center gap-2">
            <CheckCircle2 className="w-4 h-4" /> {saving ? 'Saving…' : 'Save & Continue'}
          </button>
        </div>
      </div>
    </div>
  );
}
